import queryStringify from "qs-stringify";
import { PaginationFactory, PaginatorFactory } from "../factory";
import { store } from "../store";
import { AppointmentTypes } from "../types/appointment.types";
import { api } from "../http";
import { DefaultRes } from "../types";
import { AxiosError, AxiosResponse } from "axios";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";

/** Response shape from the getEnriched endpoint */
type EnrichedResponse = {
    items: AppointmentTypes.Model[];
    page: number;
    perPage: number;
    total: number;
    summary?: AppointmentTypes.Summary;
};

export class AppointmentService {

    public static readonly dispatch = store.dispatch.appointment;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
        showUpcomingAppts = true,
        daysInThePast = 30
    ): Promise<PaginationFactory<AppointmentTypes.Model> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = AppointmentService;

        if (!ignoreState) {
            toggleLoading('fetchMany', true);
        }

        try {
            const qsfy = queryStringify({
                perPage: dto.perPage,
                page: dto.page,
                showUpcomingAppts: String(showUpcomingAppts),
                daysInThePast,
                filter: {
                    ...dto.filter,
                },
                sort: {
                    ...dto.sort,
                },
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<AppointmentTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<AppointmentTypes.Model>>>
            >(`/appointments/getMany?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<AppointmentTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Fetch enriched appointments — includes check-in/out, GPS, live status,
     * device pings, and summary stats (past tab only).
     */
    public static async fetchEnriched(
        dto: PaginatorFactory<{}>,
        tab: 'activeFuture' | 'past' = 'activeFuture',
        daysInThePast = 30,
        statusFilter?: string,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
        startDate?: string,
        endDate?: string,
    ): Promise<EnrichedResponse | void> {
        const {
            dispatch: { toggleLoading, setEnrichedPagination, setSummary },
        } = AppointmentService;

        if (!ignoreState) {
            toggleLoading('fetchEnriched', true);
        }

        try {
            const qsfy = queryStringify({
                perPage: dto.perPage,
                page: dto.page,
                tab,
                daysInThePast,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                statusFilter: statusFilter || undefined,
                filter: {
                    ...dto.filter,
                },
                sort: {
                    ...dto.sort,
                },
            });

            const {
                data: {
                    result,
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<EnrichedResponse>,
                AxiosResponse<DefaultRes<EnrichedResponse>>
            >(`/appointments/getEnriched?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchEnriched', false);

                if (isValid) {
                    setEnrichedPagination({
                        items: result.items,
                        page: result.page,
                        perPage: result.perPage,
                        total: result.total,
                    });

                    if (result.summary) {
                        setSummary(result.summary);
                    } else {
                        setSummary(null);
                    }
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return result;
        } catch (error: any) {
            if (!ignoreState) {
                toggleLoading('fetchEnriched', false);
            }

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<AppointmentTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Fetch a single enriched appointment by ID.
     * Used when opening appointment details from the alerts page.
     * Does NOT update Redux store — returns the appointment directly.
     * Returns null for legitimate not-found; throws on network/server errors
     * so callers can show the appropriate message.
     */
    public static async fetchById(
        id: string,
    ): Promise<AppointmentTypes.Model | null> {
        const {
            data: { result, isValid },
        } = await api.get<
            DefaultRes<AppointmentTypes.Model>,
            AxiosResponse<DefaultRes<AppointmentTypes.Model>>
        >(`/appointments/byId/${id}`);

        if (isValid && result) {
            return result;
        }

        return null;
    }
}